"use client";
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthProvider } from "@/app/lib/firebase/authContext";
import "@/app/globals.css";
import { AppConstants } from "@/app/lib/util/AppConstants";
import { GoogleAnalytics } from "@next/third-parties/google";
import { MainHeaderComponent } from "@/app/components/MainHeaderComponent";
import { usePathname, useRouter } from "next/navigation";
import KovaadAnalytics from "@/app/lib/analytics/KovaadAnalytics";
import { Provider } from "@/app/components/ui/provider";
import { AppToastContainer } from "@/app/lib/util/toast.client";
import IdleTimeoutHandler from "@/app/components/IdleTimeoutHandler";
import NotificationHandler from "@/app/components/notifications/NotificationHandler";
import NotificationProvider from "@/app/components/notifications/NotificationProvider";
import { useDataRefresh } from "@/app/components/hooks/useDataRefresh";
import logger from "@/lib/logger";

interface RootLayoutProps {
  children: ReactNode;
}

const RootLayout: FC<RootLayoutProps> = ({ children }) => {
  const [gaTrackingId, setGaTrackingId] = useState<string>("");
  const currentURL = usePathname() as string;
  const router = useRouter();

  // Enable global data refresh on WebSocket notifications
  useDataRefresh();

  const meta_data = useMemo(
    () => ({
      title: "KoVaaD - Expanding communication",
      description: "Empowering Communication for Autistic Children",
    }),
    [],
  );

  const eventParams = useMemo(
    () => ({
      page: meta_data.title,
      eventType: "Loaded",
      role: "guest",
    }),
    [meta_data.title],
  );

  const fetchCurrentURL = useCallback(async () => {
    setGaTrackingId(
      currentURL.includes("chat.kovaad.ai") ? "G-GSY8RY1XXW" : "G-7LSRGKHHJP",
    );
  }, [currentURL]);

  useEffect(() => {
    KovaadAnalytics.getInstance()
      .then((analytics) => {})
      .catch((error) => {
        logger.error("Error in initializing Analytics -- ", error);
      });

    KovaadAnalytics.logEvent(AppConstants.ViewsName.LANDING, eventParams);
    fetchCurrentURL().then(() => {
      logger.info("GA Tracking ID set to " + gaTrackingId);
    });
  }, [eventParams, fetchCurrentURL, gaTrackingId]);

  return (
    <html
      lang="en"
      className={`font-helvetica font-sans light`}
      suppressHydrationWarning
    >
      <head>
        <meta charSet="UTF-8" />
        {currentURL.startsWith("/trainee") ||
        currentURL.startsWith("/dashboard") ? (
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, interactive-widget=resizes-content"
          />
        ) : (
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0"
          />
        )}
        <meta name="description" content={meta_data.description} />
        <title>{meta_data.title}</title>
        <link rel="icon" href="/favicon.ico" />
      </head>
      <body>
        <AuthProvider>
          <Provider defaultTheme={"light"}>
            <NotificationProvider>
              <IdleTimeoutHandler />
              <NotificationHandler />
              <MainHeaderComponent />
              {children}
              <AppToastContainer />
            </NotificationProvider>
          </Provider>
        </AuthProvider>
      </body>
      <GoogleAnalytics gaId={gaTrackingId} />
    </html>
  );
};

export default RootLayout;
